import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Snackbar, Alert
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { templateApi } from '../../services/api';

interface Template {
  id: number;
  name: string;
  template_file: string;
  has_hero_image: boolean;
  has_sidebar: boolean;
  has_call_to_action: boolean;
  has_qnas: boolean;
  has_cards: boolean;
  has_content: boolean;
}

const TemplateList: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });

  const fetchTemplates = async () => {
    try {
      const response = await templateApi.list();
      setTemplates(response.data);
    } catch (error) {
      setNotification({ open: true, message: 'Failed to fetch templates', severity: 'error' });
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await templateApi.delete(id);
      setNotification({ open: true, message: 'Template deleted', severity: 'success' });
      fetchTemplates();
    } catch (error) {
      setNotification({ open: true, message: 'Failed to delete template', severity: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Templates</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/cms/templates/new')}>
          New Template
        </Button>
      </Box>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Template File</TableCell>
                <TableCell>Hero Image</TableCell>
                <TableCell>Sidebar</TableCell>
                <TableCell>Call to Action</TableCell>
                <TableCell>Q&A</TableCell>
                <TableCell>Cards</TableCell>
                <TableCell>Content</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((tpl) => (
                <TableRow key={tpl.id}>
                  <TableCell>{tpl.name}</TableCell>
                  <TableCell>{tpl.template_file}</TableCell>
                  <TableCell>{tpl.has_hero_image ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{tpl.has_sidebar ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{tpl.has_call_to_action ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{tpl.has_qnas ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{tpl.has_cards ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{tpl.has_content ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">
                    <IconButton color="primary" onClick={() => navigate(`/cms/templates/${tpl.id}`)}><EditIcon /></IconButton>
                    <IconButton color="error" onClick={() => handleDelete(tpl.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification({ ...notification, open: false })}>
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplateList; 